"use server";

import { cookies } from "next/headers";
import { revalidatePath } from "next/cache";
import {
  COOKIE_LAYOUTS,
  isLayoutSymbol,
  type LayoutSymbol,
} from "./types";
import { readLayoutsCookie } from "./cookies";
import { apiClient, ApiClientError } from "@/lib/api/client";
import { getImperialSession } from "@/lib/api/session";
import { revalidateCmsPageCache } from "@/lib/admin/cache";

/**
 * Server Action — przypisanie layoutu do strony przez backend (bulk-replace
 * w `imp_cms_catalog_layouts`).
 *
 * Pattern analogiczny do `saveAreaWindowsAction` (windows save) — atomowy
 * DELETE+INSERT po stronie backendu per (id_catalog, type, request_uri,
 * id_domain_language).
 *
 * **Auth:** wymaga Bearer token z SSO. Bez sesji `unauthenticated`.
 */
export async function assignLayoutAction(input: {
  layout: LayoutSymbol;
  type: 0 | 1 | 2 | 3;
  idCatalog: number | null;
  requestUri: string | null;
  lang: string;
  slug: string;
}): Promise<{ ok: boolean; error?: string }> {
  const session = await getImperialSession();
  if (!session) {
    return { ok: false, error: "unauthenticated" };
  }

  try {
    const body = {
      layout: input.layout,
      type: input.type,
      id_catalog: input.idCatalog,
      request_uri: input.requestUri,
    };
    await apiClient<{ data: { id: number | null; layout: string } }>(
      "/IRESTAPI/v1/IWINDOWS_MANAGER_CONTROLLER/assignLayout",
      {
        method: "POST",
        bearerToken: session.accessToken,
        lang: input.lang,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    await revalidateCmsPageCache(input.slug);
    return { ok: true };
  } catch (e) {
    if (e instanceof ApiClientError) {
      return { ok: false, error: e.message };
    }
    return { ok: false, error: "unexpected_error" };
  }
}

/**
 * Server Action — zapisuje wybór layoutu dla konkretnego slug'a do cookie map.
 *
 * Kolejne wywołania merge'ują z istniejącą mapą (nie nadpisują):
 *   - `setLayoutForSlug("/", "LSingleColumn")` ustawia tylko homepage
 *   - `setLayoutForSlug("/o-nas", "LCatalogTwoColsLeft")` ustawia tylko `/o-nas`
 *
 * Po zapisie revalidatePath konkretnego slug'a (lub całego layout'u dla "/").
 *
 * @see ../../.claude/CLAUDE.md sekcja "Architektura layoutów"
 */
export async function setLayoutForSlugAction(
  slug: string,
  symbol: LayoutSymbol,
): Promise<{ ok: boolean; error?: string }> {
  if (!isLayoutSymbol(symbol)) {
    return { ok: false, error: `Invalid layout symbol: ${symbol}` };
  }
  if (typeof slug !== "string" || slug.length === 0) {
    return { ok: false, error: `Invalid slug: ${slug}` };
  }

  const existing = await readLayoutsCookie();
  const next = { ...existing, [slug]: symbol };

  const cookieStore = await cookies();
  cookieStore.set(COOKIE_LAYOUTS, JSON.stringify(next), {
    path: "/",
    maxAge: 60 * 60 * 24 * 365, //1 year
    sameSite: "lax",
    httpOnly: false,
  });

  revalidatePath(slug, "layout");
  return { ok: true };
}
